import React, { useState, useEffect, useRef, useCallback } from 'react';

const servicesData = [
  {
    image: '/assets/images/service-bpo.png',
    title: 'BPO Services',
    description: 'Our Business Process Outsourcing service includes Inbound and Outbound call centers ensuring customer satisfaction and increased conversion.',
  },
  {
    image: '/assets/images/service-it.png',
    title: 'IT Services',
    description: 'We offer a complete range of IT services. Our service includes software development, secure cloud solutions, cybersecurity, software and quality assurance to help you lead the world.',
  },
  {
    image: '/assets/images/service-webdev.png',
    title: 'Website Development',
    description: 'We build websites with the latest technologies and best practices. We want to meet the present day business requirements with our web development services.',
  },
  {
    image: '/assets/images/service-design.png',
    title: 'Website Design',
    description: 'Our internet marketing company in Karachi has a complete team of web designers. Who create the entire website for your brand to build a very strong and attractive web presence.',
  },
  {
    image: '/assets/images/service-marketing.png',
    title: 'Digital Marketing',
    description: 'As a leading digital marketing company, we also provide digital marketing services to help you create a strong online presence. We use various tools and techniques to advertise your brand via SEO, Social Media, and Local SEO.',
  },
  {
    image: '/assets/images/service-logo.png',
    title: 'Logo Design',
    description: 'Stand out with a unique brand identity with our expert graphics and logo designing services.',
  }
];

// Create a triply-extended list to facilitate seamless infinite looping
const extendedServices = [...servicesData, ...servicesData, ...servicesData];

function Services({ setView }) {
  // Start the slider in the middle copy (index 6, which is BPO Services of the second set)
  const [currentIndex, setCurrentIndex] = useState(servicesData.length);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [itemsVisible, setItemsVisible] = useState(3);
  const trackRef = useRef(null);

  // Dragging states
  const isDragging = useRef(false);
  const startX = useRef(0);
  const currentX = useRef(0);
  const dragTranslate = useRef(0);

  const handleNavClick = (e, view, path) => {
    e.preventDefault();
    if (setView) {
      window.history.pushState({}, '', path);
      setView(view);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.location.href = path;
    }
  };

  const getItemsVisibleCount = useCallback(() => {
    if (window.innerWidth > 1024) return 3;
    if (window.innerWidth > 768) return 2;
    return 1;
  }, []);

  const getCardWidth = useCallback(() => {
    if (trackRef.current) {
      return trackRef.current.offsetWidth / itemsVisible;
    }
    return 0;
  }, [itemsVisible]);

  const updatePosition = useCallback((withTransition = true) => {
    if (trackRef.current) {
      const cardWidth = getCardWidth();
      trackRef.current.style.transition = withTransition 
        ? 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)' 
        : 'none';
      trackRef.current.style.transform = `translateX(-${currentIndex * cardWidth}px)`;
      dragTranslate.current = -currentIndex * cardWidth;
    }
  }, [currentIndex, getCardWidth]);

  useEffect(() => {
    setItemsVisible(getItemsVisibleCount());
    const handleResize = () => {
      setItemsVisible(getItemsVisibleCount());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [getItemsVisibleCount]);

  useEffect(() => {
    updatePosition(!isDragging.current && !isTransitioning);
  }, [currentIndex, updatePosition, isTransitioning]);

  const slideNext = useCallback(() => {
    if (isTransitioning || isDragging.current) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev + 1);
  }, [isTransitioning]);

  // eslint-disable-next-line no-unused-vars
  const slidePrev = useCallback(() => {
    if (isTransitioning || isDragging.current) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev - 1);
  }, [isTransitioning]);

  // Clean boundary jumps to create the infinite loop illusion
  const handleTransitionEnd = () => {
    setIsTransitioning(false);
    
    // If we reach the third set, jump back to the second set silently
    if (currentIndex >= servicesData.length * 2) {
      if (trackRef.current) {
        trackRef.current.style.transition = 'none';
      }
      setCurrentIndex(currentIndex - servicesData.length);
    }
    // If we reach the first set, jump forward to the second set silently
    else if (currentIndex < servicesData.length) {
      if (trackRef.current) {
        trackRef.current.style.transition = 'none';
      }
      setCurrentIndex(currentIndex + servicesData.length);
    }
  };

  // Auto scroll
  useEffect(() => {
    const timer = setInterval(() => {
      slideNext();
    }, 4500);
    return () => clearInterval(timer);
  }, [slideNext]);

  // Touch / Drag events for hand-slide
  const handleStart = (e) => {
    if (isTransitioning) return;
    isDragging.current = true;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    startX.current = clientX;
    if (trackRef.current) {
      trackRef.current.style.transition = 'none';
    }
  };

  const handleMove = (e) => {
    if (!isDragging.current) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    currentX.current = clientX - startX.current;
    
    if (trackRef.current) {
      const currentTranslate = dragTranslate.current + currentX.current;
      trackRef.current.style.transform = `translateX(${currentTranslate}px)`;
    }
  };

  const handleEnd = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    
    const cardWidth = getCardWidth();
    const threshold = cardWidth * 0.25;

    if (trackRef.current) {
      trackRef.current.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      
      if (currentX.current < -threshold) {
        // Dragged left -> go next
        setIsTransitioning(true);
        setCurrentIndex((prev) => prev + 1);
      } else if (currentX.current > threshold) {
        // Dragged right -> go prev
        setIsTransitioning(true);
        setCurrentIndex((prev) => prev - 1);
      } else {
        // Snap back to current
        updatePosition(true);
      }
    }
    currentX.current = 0;
  };

  // Dots navigation
  const handleDotClick = (index) => {
    if (isTransitioning || isDragging.current) return;
    setIsTransitioning(true);
    setCurrentIndex(servicesData.length + index);
  };

  return (
    <section className="services-section">
      <div className="container">
        {/* Section Header */}
        <div className="services-header">
          <div className="services-title-area">
            <div className="services-label">
              <span className="label-dots">
                <span className="dot-lime"></span>
                <span className="dot-dark"></span>
              </span>
              Our Services
            </div>
            <h2 className="services-main-heading">Boost Your Brand with Our Expertise</h2>
          </div>
          <div className="services-control-area">
            <button className="btn-services-view" onClick={(e) => handleNavClick(e, 'services', '/services/')}>
              View All Services 
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </button>
          </div>
        </div>

        {/* Carousel Wrapper */}
        <div className="services-carousel-wrapper">
          <div 
            className="services-track" 
            ref={trackRef}
            onTransitionEnd={handleTransitionEnd}
            onMouseDown={handleStart}
            onMouseMove={handleMove}
            onMouseUp={handleEnd}
            onMouseLeave={handleEnd}
            onTouchStart={handleStart}
            onTouchMove={handleMove}
            onTouchEnd={handleEnd}
          >
            {extendedServices.map((service, index) => {
              // Highlight the visible card on mobile (1 card) or second visible card on desktop/tablet
              const isHighlighted = itemsVisible === 1 ? index === currentIndex : index === currentIndex + 1;
              const serviceConfig = 
                service.title === 'BPO Services' ? { view: 'bpo-services', path: '/bpo-services/' } :
                service.title === 'IT Services' ? { view: 'it-services', path: '/it-services/' } :
                service.title === 'Website Development' ? { view: 'web-development', path: '/web-development/' } :
                service.title === 'Website Design' ? { view: 'web-design', path: '/web-design/' } :
                service.title === 'Digital Marketing' ? { view: 'digital-marketing', path: '/digital-marketing/' } :
                service.title === 'Logo Design' ? { view: 'logo-design', path: '/logo-design/' } : { view: 'services', path: '/services/' };

              return (
                <div 
                  className={`service-card-column ${isHighlighted ? 'highlighted' : ''}`}
                  key={`${service.title}-${index}`}
                >
                  <div className="service-card-wrapper">
                    {/* Top Image Part */}
                    <div className="service-card-image-box">
                      <img 
                        src={service.image} 
                        alt={service.title} 
                        className="service-card-img" 
                      />
                    </div>
                    {/* Bottom Info Part */}
                    <div className="service-card-info-box">
                      <h3>{service.title}</h3>
                      <p>{service.description}</p>
                      <a 
                        href={serviceConfig.path} 
                        onClick={(e) => handleNavClick(e, serviceConfig.view, serviceConfig.path)} 
                        className="service-card-link"
                      >
                        Learn more 
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Navigation Indicator Dots */}
        <div className="services-dots">
          {servicesData.map((_, index) => {
            // Find active dot index based on current active card in view
            const activeDotIndex = (currentIndex - servicesData.length) % servicesData.length;
            const normalizedActiveIndex = activeDotIndex < 0 ? activeDotIndex + servicesData.length : activeDotIndex;
            return (
              <button 
                key={index} 
                className={`services-dot ${index === normalizedActiveIndex ? 'active' : ''}`}
                onClick={() => handleDotClick(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default Services;
